// src/components/navigation/Menus.js
import React, { useEffect, useState } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import { useAppData } from '../AppDataContext';
import { API_URL } from '../../../config';
import { useResponsiveStyles } from './useResponsiveStyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

/* ---------- Helpers: normalize API → subMenus tree ---------- */

const buildCategoryNode = (cat) => ({
  id: cat.id,
  title: cat.name,
  slug: cat.slug,
  description: cat.description,
  link: null,
  link_type: 'category',
  menu_type: 'category',
  level: cat.level,
  subMenus: Array.isArray(cat.subCategories)
    ? cat.subCategories.map(buildCategoryNode)
    : [],
});

const normalizeMenus = (apiMenus) =>
  apiMenus.map((m) => {
    // Convert link_type: 'category' + category tree into a mega root with subMenus
    if (m.link_type === 'category' && m.category) {
      return {
        id: m.category.id,
        title: m.title || m.category.name,
        slug: m.category.slug,
        link: null,
        link_type: 'category',
        menu_type: m.menu_type || 'mega_menu',
        level: m.level ?? 0,
        subMenus: Array.isArray(m.category.subCategories)
          ? m.category.subCategories.map(buildCategoryNode)
          : [],
      };
    }
    // Ensure subMenus array exists for other menu types
    return { ...m, subMenus: Array.isArray(m.subMenus) ? m.subMenus : [] };
  });

/* ---------- Component ---------- */

const Menus = ({
  selectedMenus = [],
  styles = {},
  template = 'Horizontal',
}) => {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { subdomain } = useAppData();

  // Responsive style hooks
  const appliedStyles = useResponsiveStyles(styles);
  const menuStyles = appliedStyles?.menu || {};
  const menuItemStyles = appliedStyles?.menuItem || {};
  const menuItemLink = appliedStyles?.menuItemLink || {};
  const subMenuStyles = appliedStyles?.subMenu || {};
  const dropdownItemStylesLink = appliedStyles?.dropdownItemStylesLink || {};
  const dropdownItemStyles = appliedStyles?.dropdownItem || {};
  const spinnerStyles = appliedStyles?.spinner || {};
  const alertStyles = appliedStyles?.alert || {};
  const noMenusMessageStyles = appliedStyles?.noMenusMessage || {};

  /* ---------- Data fetch ---------- */
  useEffect(() => {
    const fetchMenus = async () => {
      try {
        setLoading(true);
        const menuIds = selectedMenus.map((m) => m.value);
        const url = `${API_URL}/menus/filter?account_key=${subdomain}&menus_id=${menuIds.join(',')}`;
        const resp = await fetch(url);
        const data = await resp.json();
        if (!resp.ok || !Array.isArray(data)) {
          throw new Error(data?.message || 'Failed to fetch menus.');
        }
        setMenus(normalizeMenus(data));
      } catch (e) {
        console.error('Error fetching menus:', e);
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    if (selectedMenus.length > 0) {
      fetchMenus();
    } else {
      setMenus([]);
      setLoading(false);
    }
  }, [selectedMenus, subdomain]);

  /* ---------- URL builder ---------- */
  const getMenuLink = (menu) => {
    switch (menu.link_type) {
      case 'category':
        return `/products?cat=${encodeURIComponent(menu.slug || menu.id)}`;
      case 'product':
      case 'productListingPage':
        return '/products';
      case 'blog':
        return `/blog/${menu.blog_id}`;
      default:
        return menu.link || '#';
    }
  };

  /* ---------- Renderers ---------- */

  // Simple dropdown for non-mega items (kept for compatibility)
  const renderHorizontalSubMenuItems = (subMenus) =>
    subMenus.map((subMenu) => {
      const hasSub = subMenu.subMenus && subMenu.subMenus.length > 0;
      return (
        <li
          key={subMenu.id}
          className={`dropdown-item ${hasSub ? 'has-dropdown' : ''}`}
          style={dropdownItemStyles}
        >
          <a href={getMenuLink(subMenu)} style={dropdownItemStylesLink}>
            {hasSub && <FontAwesomeIcon icon={faChevronDown} className="dropdown-icon" />}
            {subMenu.title}
          </a>
          {hasSub && (
            <ul className="dropdown" style={subMenuStyles}>
              {renderHorizontalSubMenuItems(subMenu.subMenus)}
            </ul>
          )}
        </li>
      );
    });

  // Mega menu like Myntra/Flipkart:
  // Level-1 → columns; Level-2 → section headers; Level-3 → link lists
  const renderMegaMenu = (menu) => (
    <div className="mega-menu" role="menu" aria-label={`${menu.title} categories`}>
      <div className="mega-grid">
        {menu.subMenus.map((col) => (
          <div key={col.id} className="mega-col">
            {/* Level-1 (column) */}
            <a className="mega-col-title" href={getMenuLink(col)}>
              {col.title}
            </a>

            {/* Level-2 (sections) */}
            {Array.isArray(col.subMenus) && col.subMenus.length > 0 && (
              <div className="mega-sections">
                {col.subMenus.map((section) => (
                  <div key={section.id} className="mega-section">
                    <a className="mega-section-title" href={getMenuLink(section)}>
                      {section.title}
                    </a>

                    {/* Level-3 (leaf links) */}
                    {Array.isArray(section.subMenus) && section.subMenus.length > 0 && (
                      <ul className="mega-links">
                        {section.subMenus.map((leaf) => (
                          <li key={leaf.id}>
                            <a href={getMenuLink(leaf)}>{leaf.title}</a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // Horizontal list with mega support
  const renderHorizontalMenuItems = (menusList) =>
    menusList.map((menu) => {
      const hasSub = menu.subMenus && menu.subMenus.length > 0;
      const isMega = menu.menu_type === 'mega_menu' && hasSub;

      return (
        <li
          key={menu.id}
          className={`menu-item ${hasSub ? (isMega ? 'has-mega' : 'has-dropdown') : ''}`}
          style={menuItemStyles}
        >
          <a href={getMenuLink(menu)} style={menuItemLink}>
            {menu.title}
            {hasSub && <FontAwesomeIcon icon={faChevronDown} className="dropdown-icon" />}
          </a>

          {hasSub &&
            (isMega ? (
              renderMegaMenu(menu)
            ) : (
              <ul className="dropdown" style={subMenuStyles}>
                {renderHorizontalSubMenuItems(menu.subMenus)}
              </ul>
            ))}
        </li>
      );
    });

  // Vertical renderers (optional; unchanged)
  const renderVerticalSubMenuItems = (subMenus) => (
    <ul className="submenu" style={subMenuStyles}>
      {subMenus.map((sm) => {
        const hasSub = sm.subMenus && sm.subMenus.length > 0;
        return (
          <li key={sm.id} className="submenu-item" style={dropdownItemStyles}>
            {hasSub ? (
              <>
                <span style={{ cursor: 'pointer' }}>
                  {sm.title} <FontAwesomeIcon icon={faChevronDown} className="dropdown-icon" />
                </span>
                {renderVerticalSubMenuItems(sm.subMenus)}
              </>
            ) : (
              <a href={getMenuLink(sm)} style={dropdownItemStyles}>
                {sm.title}
              </a>
            )}
          </li>
        );
      })}
    </ul>
  );

  const renderVerticalMenuItems = (menusList) => (
    <ul className="menu" style={menuStyles}>
      {menusList.map((m) => {
        const hasSub = m.subMenus && m.subMenus.length > 0;
        return (
          <li key={m.id} className="menu-item" style={menuItemStyles}>
            {hasSub ? (
              <>
                <span style={{ cursor: 'pointer' }}>
                  {m.title} <FontAwesomeIcon icon={faChevronDown} className="dropdown-icon" />
                </span>
                {renderVerticalSubMenuItems(m.subMenus)}
              </>
            ) : (
              <a href={getMenuLink(m)} style={menuItemLink}>
                {m.title}
              </a>
            )}
          </li>
        );
      })}
    </ul>
  );

  /* ---------- States ---------- */
  if (loading) {
    return (
      <div className="text-center my-4" style={spinnerStyles}>
        <Spinner animation="border" role="status" style={spinnerStyles}>
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-center my-4" style={alertStyles}>
        <Alert variant="danger" style={alertStyles}>
          {error}
        </Alert>
      </div>
    );
  }
  if (menus.length === 0) {
    return (
      <div className="text-center my-4" style={noMenusMessageStyles}>
        <p style={noMenusMessageStyles}>No menus found.</p>
      </div>
    );
  }

  /* ---------- Render ---------- */
  return template === 'Vertical' ? (
    <nav className="vertical-menu-container">
      {renderVerticalMenuItems(menus)}
    </nav>
  ) : (
    <nav className="horizontal-menu-container">
      <ul className="menu" style={menuStyles}>
        {renderHorizontalMenuItems(menus)}
      </ul>
    </nav>
  );
};

export default Menus;
